'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
require('../../DataDisplays/Avatar/Avatar.js');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');
var TextFieldContainer = require('../TextFieldContainer/TextFieldContainer.js');
var Calendar_parts = require('./Calendar.parts.js');
var Calendar_utils = require('./Calendar.utils.js');

/**
 * 날짜를 선택할 수 있는 캘린더 컴포넌트입니다.
 *
 * @component

 *
 * @param {Object} props - 컴포넌트 프로퍼티
 * @param {string} [props.selected] - 선택된 날짜 (YYYY-MM-DD 형식)
 * @param {(date: string) => void} [props.onChange] - 날짜 선택 시 호출되는 콜백 함수, YYYY-MM-DD 형식의 인자를 받음
 * @param {string} [props.defaultDate] - 초기 기본 날짜 (YYYY-MM-DD 형식)
 * @param {boolean} [props.disabled] - 캘린더 비활성화 여부
 * @param {string} [props.placeholder] - 날짜가 선택되지 않았을 때 표시될 텍스트
 * @param {boolean} [props.isError] - 에러 상태 표시 여부
 *
 * @example
 * ```tsx
 * // 기본 사용법
 * <Calendar
 *   selected="2024-03-21"
 *   onChange={(date) => console.log(date)}
 *   placeholder="날짜를 선택하세요"
 * />
 *
 * // 비활성화된 상태
 * <Calendar
 *   disabled={true}
 *   placeholder="선택 불가"
 * />
 *
 * // 에러 상태
 * <Calendar
 *   isError={true}
 *   selected="2024-03-21"
 * />
 * ```
 */ const Calendar = ({ defaultDate, selected, onChange, disabled, placeholder, isError })=>{
    const [isOpen, setIsOpen] = react.useState(false);
    const openHandler = ()=>{
        if (disabled) return;
        setIsOpen((pre)=>!pre);
    };
    const organizedDefaultDate = defaultDate ? Calendar_utils.generateDate(defaultDate) : Calendar_utils.today;
    const [visibleDate, setVisibleDate] = react.useState(selected ? Calendar_utils.generateDate(selected) : organizedDefaultDate);
    const selectedDate = selected ? Calendar_utils.generateDate(selected) : organizedDefaultDate;
    const { year: koreanYear, month: koreanMonth, date: koreanDate } = Calendar_utils.getKoreanTimeString(selectedDate);
    const { dateList } = Calendar_utils.getMonthDate(visibleDate.year, visibleDate.month);
    const initialArray = Array.from({
        length: dateList[0].day
    }, ()=>null);
    const organizedDateList = [
        ...initialArray,
        ...dateList
    ];
    const onChangeSelectedDate = (date)=>{
        if (selected && Calendar_utils.isSameDate(date, selectedDate)) return;
        const dateString = `${date.year}-${date.month}-${date.date}`;
        onChange === null || onChange === void 0 ? void 0 : onChange(dateString);
        setIsOpen(false);
    };
    const onChangeVisibleDate = (date)=>{
        setVisibleDate(date);
    };
    react.useEffect(()=>{
        if (!isOpen && selected) {
            setVisibleDate(Calendar_utils.generateDate(selected));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen
    ]);
    return jsxRuntime.jsxs(Box, {
        className: "relative h-fit w-fit select-none",
        "data-testid": "design-system-calendar--container",
        children: [
            jsxRuntime.jsx(Box, {
                className: "cursor-pointer",
                children: jsxRuntime.jsxs(TextFieldContainer, {
                    isFocused: isOpen,
                    isError: isError,
                    isDisabled: disabled,
                    testId: "design-system-calendar--trigger",
                    onClick: openHandler,
                    children: [
                        jsxRuntime.jsx(SystemIcon, {
                            name: "medium_calendar",
                            className: disabled || !selected ? 'text-w-gray-300' : 'text-w-gray-400'
                        }),
                        jsxRuntime.jsx(Typography, {
                            variant: "body1",
                            className: tailwindMerge.twMerge('min-w-[178px] text-w-gray-600', !selected && 'text-w-gray-300', disabled && 'text-w-gray-300'),
                            "data-testid": "design-system-calendar--selected-date",
                            children: selected ? `${koreanYear} ${koreanMonth} ${koreanDate}` : placeholder
                        })
                    ]
                })
            }),
            isOpen && jsxRuntime.jsx("div", {
                className: "fixed left-0 top-0 z-10 h-screen w-screen bg-transparent",
                onClick: openHandler
            }),
            isOpen && jsxRuntime.jsxs(Box, {
                "data-testid": "design-system-calendar--content",
                className: "absolute top-[54px] z-30 flex w-[350px] flex-col gap-4 rounded-xl border border-w-gray-200 bg-w-white px-[15px] py-[23px] shadow-graymedium",
                children: [
                    jsxRuntime.jsx(Calendar_parts.SelectHeader, {
                        visibleDate: visibleDate,
                        onChange: onChangeVisibleDate
                    }),
                    jsxRuntime.jsx(Calendar_parts.WeekHeader, {}),
                    jsxRuntime.jsx(Box, {
                        className: "grid w-full grid-cols-7 place-content-center place-items-center gap-1",
                        children: organizedDateList.map((item, index)=>{
                            const isSelected = item && selected ? Calendar_utils.isSameDate(item, selectedDate) : false;
                            return item ? jsxRuntime.jsx(Calendar_parts.DateBox, {
                                date: item,
                                selected: isSelected,
                                onChange: onChangeSelectedDate
                            }, `${item.year}-${item.month}-${item.date}`) : jsxRuntime.jsx(Calendar_parts.DateBox, {
                                disabled: true
                            }, index);
                        })
                    })
                ]
            })
        ]
    });
};

module.exports = Calendar;
